import { Flex, Box, Text, Button } from "@radix-ui/themes";
import { useRef, useState } from "react";
import UploadImagePanel from "@/components/image/panels/UploadImagePanel";
import LibraryImagePanel from "@/components/image/panels/LibraryImagePanel";
import GenerativeImagePanel from "@/components/image/panels/GenerativeImagePanel";
import { ImageCropperHandle } from "@/components/image/ImageCropper";
import { cn } from "@/lib/utils";
import type { ImageSourceDto } from "@/lib/lutely-api/gen/dtos/imageSourceDto";
import type { ImageApplyTarget } from "./types";
import { SparksAIIcon, SwapSwitchIcon } from "@/assets/icons";
import { Modal } from "@/components/modal/Modal";

type AddImageModalProps = {
  creatorId: string;
  target: ImageApplyTarget;
  onClose: () => void;
  onUpload: (file: File) => Promise<void> | void;
  onSelectLibraryImage: (source: ImageSourceDto) => Promise<void>;
  onSaved?: () => void;
  isSaving: boolean;
  circular?: boolean;
  title?: string;
};

type ImagePanel = "upload" | "library" | "generative";

export function AddImageModal({
  creatorId,
  target,
  onClose,
  onUpload,
  onSelectLibraryImage,
  onSaved,
  isSaving,
  circular,
  title,
}: AddImageModalProps) {
  const [activePanel, setActivePanel] = useState<ImagePanel>("upload");
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [hasUnsavedGenerations, setHasUnsavedGenerations] = useState(false);
  const [hasUnsavedUpload, setHasUnsavedUpload] = useState(false);
  const [pendingPanel, setPendingPanel] = useState<ImagePanel | null>(null);
  const cropperRef = useRef<ImageCropperHandle | null>(null);
  const [selectedSource, setSelectedSource] = useState<ImageSourceDto | null>(
    null,
  );

  async function handleSave() {
    if (isSaving) return;

    if (activePanel === "upload") {
      await cropperRef.current?.emitCroppedImage();
      return;
    }

    if (activePanel === "library" && selectedSource) {
      await onSelectLibraryImage(selectedSource);
      onSaved?.();
      onClose();
      return;
    }
  }

  async function handleCropComplete(file: File) {
    await onUpload(file);
    onSaved?.();
    onClose();
  }

  function switchPanel(panel: ImagePanel) {
    setActivePanel(panel);
    setSelectedSource(null);
    setImageSrc(null);
    setHasUnsavedUpload(false);
  }

  function hasUnsavedChanges() {
    return (
      (activePanel === "generative" && hasUnsavedGenerations) ||
      (activePanel === "upload" && hasUnsavedUpload)
    );
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && hasUnsavedChanges()) {
      setShowDiscardConfirm(true);
      return;
    }

    if (!nextOpen) {
      onClose();
    }
  }

  function requestPanelSwitch(panel: ImagePanel) {
    if (panel === activePanel) return;

    if (hasUnsavedChanges()) {
      setPendingPanel(panel);
      setShowDiscardConfirm(true);
      return;
    }

    switchPanel(panel);
  }

  function handleDiscard() {
    setShowDiscardConfirm(false);
    setHasUnsavedGenerations(false);
    setHasUnsavedUpload(false);

    if (pendingPanel) {
      switchPanel(pendingPanel);
      setPendingPanel(null);
      return;
    }

    onClose();
  }

  return (
    <Modal
      title={title ?? "Add artwork"}
      size="smd"
      open
      onOpenChange={handleOpenChange}
    >
      {/* Tabs */}
      <Flex gap="4" className="border-b-[1px] border-[#050C2B14] mb-5">
        <Button
          disabled={isSaving}
          data-testid="image-modal-tab-upload"
          className={cn(
            "text-[14px] text-[#525966] font-semibold bg-transparent px-2 pb-3 rounded-none",
            activePanel === "upload" && [
              "text-blue-900",
              "border-b-[3px]",
              "border-brand-primary",
              "[border-bottom-style:solid]",
            ],
          )}
          onClick={() => requestPanelSwitch("upload")}
        >
          Upload
        </Button>

        <Button
          disabled={isSaving}
          data-testid="image-modal-tab-library"
          className={cn(
            "text-[14px] text-[#525966] font-semibold bg-transparent px-2 pb-3 rounded-none",
            activePanel === "library" && [
              "text-blue-900",
              "border-b-[3px]",
              "border-brand-primary",
              "[border-bottom-style:solid]",
            ],
          )}
          onClick={() => requestPanelSwitch("library")}
        >
          Library
        </Button>

        <Flex
          direction="row"
          className={cn(
            "rounded-none",
            activePanel === "generative" && [
              "border-b-[3px]",
              "border-brand-primary",
              "[border-bottom-style:solid]",
            ],
          )}
        >
          <SparksAIIcon
            className={cn(
              "w-4 h-4 fill-[#525966]",
              activePanel === "generative" && "fill-blue-900",
            )}
          />
          <Button
            disabled={isSaving}
            className={cn(
              "text-[14px] text-[#525966] font-semibold bg-transparent pl-1 pr-2 pb-3 rounded-none",
              activePanel === "generative" && "text-blue-900",
            )}
            onClick={() => requestPanelSwitch("generative")}
          >
            Create
          </Button>
        </Flex>
      </Flex>

      {activePanel === "upload" && (
        <UploadImagePanel
          imageSrc={imageSrc}
          onSelectImage={(src) => {
            setImageSrc(src);
            setHasUnsavedUpload(true);
          }}
          cropperRef={cropperRef}
          onCropComplete={handleCropComplete}
          circular={circular}
        />
      )}

      {activePanel === "library" && (
        <LibraryImagePanel
          creatorId={creatorId}
          onSelect={(source) => setSelectedSource(source)}
        />
      )}

      {activePanel === "generative" && (
        <GenerativeImagePanel
          creatorId={creatorId}
          onApplied={() => {
            onSaved?.();
            onClose();
          }}
          target={target}
          onUnsavedChange={setHasUnsavedGenerations}
        />
      )}

      {(activePanel === "library" ||
        (activePanel === "upload" && imageSrc)) && (
        <Flex
          justify={{ initial: "between", sm: "end" }}
          className="pt-[14px] items-center"
        >
          {activePanel === "upload" && imageSrc ? (
            <Button
              variant="ghost"
              className="sm:hidden pl-3"
              onClick={() => cropperRef.current?.replaceImage()}
            >
              <SwapSwitchIcon />
              Replace image
            </Button>
          ) : (
            <span />
          )}
          <Button
            onClick={handleSave}
            data-testid="image-modal-save-button"
            disabled={
              isSaving ||
              (activePanel === "upload"
                ? !imageSrc
                : activePanel === "library"
                  ? !selectedSource
                  : true)
            }
            loading={isSaving}
            size="3"
            radius="full"
          >
            Save
          </Button>
        </Flex>
      )}

      {showDiscardConfirm && (
        <Flex
          position="absolute"
          inset="0"
          align="center"
          justify="center"
          className="z-50 bg-white/85"
        >
          <Box className="bg-white rounded-md shadow-lg p-6 w-[320px]">
            <Flex direction="column" gap="4">
              <Text size="3" className="font-semibold">
                Discard unsaved changes?
              </Text>

              <Text size="2" className="text-[#525966]">
                Your unsaved changes will be lost if you continue.
              </Text>

              <Flex justify="end" align="center" gap="4">
                <Button
                  className="text-blue-800 px-1 hover:text-brand-primary"
                  variant="ghost"
                  size="2"
                  onClick={() => {
                    setShowDiscardConfirm(false);
                    setPendingPanel(null);
                  }}
                >
                  No
                </Button>

                <Button
                  className="px-3 bg-red-600 hover:bg-red-500"
                  radius="full"
                  onClick={handleDiscard}
                >
                  Yes, Discard
                </Button>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      )}
    </Modal>
  );
}
